import { Injectable } from '@angular/core';

import { Observable, forkJoin as observableForkJoin, of as observableOf } from 'rxjs';
import { map } from 'rxjs/operators';

import { EndpointsConstants } from 'app/shared/constants/endpoints.constants';

import { HttpClientService } from 'app/core/services/http-client.service';
import { ClientService } from 'app/client/services/client.service';
import { MachineService } from 'app/machine/services/machine.service';
import { SellerService } from 'app/seller/services/seller.service';
import { ConcessionaireService } from 'app/concessionaire/services/concessionaire.service';

import { ResponseMetadata, AssemblyOrder, Machine, Dealership, Seller, Client } from 'app/model';

@Injectable()
export class AssemblyOrderService {

  constructor(
    private httpService: HttpClientService,
    private machineService: MachineService,
    private clientService: ClientService,
    private dealershipService: ConcessionaireService,
    private sellersService: SellerService
  ) { }

  getAllAssemblyOrders(params?): Observable<{ assemblyOrders: AssemblyOrder[], metadata: ResponseMetadata }> {
    return this.httpService
      .get<{ assemblyOrders: AssemblyOrder[], metadata: ResponseMetadata }>(EndpointsConstants.assemblyOrder.all, params)
      .pipe(map(response => {
        return {
          assemblyOrders: response['data'].assemblyOrders.map(assemblyOrder => new AssemblyOrder(assemblyOrder)),
          metadata: response['data'].metadata
        };
      }));
  }

  getAssemblyOrder(id: string): Observable<AssemblyOrder> {
    const endpointUrl = EndpointsConstants.assemblyOrder.single.replace('{id}', id);
    return this.httpService.get<AssemblyOrder>(endpointUrl).pipe(map(response => new AssemblyOrder(response['data'])));
  }

  getOrphanAssemblyOrders(): Observable<AssemblyOrder[]> {
    return this.httpService
      .get<AssemblyOrder[]>(EndpointsConstants.assemblyOrder.orphans)
      .pipe(map(response => response['data']['assemblyOrders'].map(assemblyOrder => new AssemblyOrder(assemblyOrder))));
  }

  saveAssemblyOrder(assemblyOrder): Observable<AssemblyOrder> {
    return this.httpService.post(EndpointsConstants.assemblyOrder.all, assemblyOrder)
      .pipe(map(response => new AssemblyOrder(response['data'])));
  }

  updateAssemblyOrders(id: string, editedAssemblyOrder): Observable<AssemblyOrder> {
    const endpointUrl = EndpointsConstants.assemblyOrder.single.replace('{id}', id);
    return this.httpService.put(endpointUrl, editedAssemblyOrder).pipe(map(response => new AssemblyOrder(response['data'])));
  }

  deleteAssemblyOrder(id: string): Observable<{ message: string }> {
    const endpointUrl = EndpointsConstants.assemblyOrder.single.replace('{id}', id);
    return this.httpService.delete<{ message: string }>(endpointUrl);
  }

  getAssemblyOrderResources(idAssemblyOrder?: string) {
    return observableForkJoin(
      idAssemblyOrder ? this.getAssemblyOrder(idAssemblyOrder) : observableOf(null),
      this.clientService.getAllClients(),
      this.machineService.getAllMachines(),
      this.dealershipService.getAllDealerships(),
      this.sellersService.getAllSellers()
    ).pipe(map((data: [
      { assemblyOrder?: AssemblyOrder },
      { clients: Client[], metadata: ResponseMetadata },
      { machines: Machine[], metadata: ResponseMetadata },
      { dealerships: Dealership[], metadata: ResponseMetadata },
      { sellers: Seller[], metadata: ResponseMetadata }
    ]) => {
      const [ assemblyOrder, clientsData, machinesData, dealershipsData, sellersData ] = data;

      const clients = clientsData.clients;
      const machines = machinesData.machines;
      const dealerships = dealershipsData.dealerships;
      const sellers = sellersData.sellers;

      return { assemblyOrder, clients, machines, dealerships, sellers };
    }));
  }

  uploadDetails(file) {
    const formData: FormData = new FormData();
    formData.append('details', file);
    return this.httpService.post(EndpointsConstants.assemblyOrder.uploadDetails, formData)
      .pipe(map(response => response['data']['url']));
  }
}
